import React, { useState, useEffect, FC, useMemo, useRef } from 'react';
import './preview-table.scss';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import PreviewTableRow from '../preview-table-row/preview-table-row';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { getShopsByUser } from '../../shopSlices';
import { getDetailedShops } from '../../shopSlices';

// Add these types at the top
type SortField = string;  // Dynamic based on headers
type SortOrder = 'asc' | 'desc';

interface PreviewTableProps{
    objectList:any[],
    headers:any[],
    setPopupOpen?:Function
}
const PreviewTable:FC<PreviewTableProps> = ({objectList, headers, setPopupOpen}) => {
    const dispatch : Function = useDispatch();
    const [search, setSearch] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState<string[]>(() => {
        const saved = localStorage.getItem(`preview_filters_${window.location.pathname}`);
        return saved ? JSON.parse(saved) : [];
    });
    const filterContainerRef = useRef<HTMLDivElement>(null);
    const [checkedStocks, setCheckedStocks] = useState([]);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const user = useSelector((state: any) => state.auth.user);
    const userAllowedToEdit = user.roleLevel >= 2;
    const ic_products = useSelector((state: any) => state.product.cumulative_ic_products);
    const hasICProducts = ic_products && ic_products.length > 0;
    const global_total_quantity = objectList?.reduce((accumulator ,item) => {
        return accumulator += item.total_quantity;
        }, 0)
    const global_total_liters = objectList?.reduce((accumulator ,item) => {
        return accumulator += item.total_liters;
        }, 0)
    const global_three_liter = objectList?.reduce((accumulator ,item) => {
        return accumulator += item.three_liter;
        }, 0)
    const global_five_liter = objectList?.reduce((accumulator ,item) => {
        return accumulator += item.five_liter;
        }, 0)
    const global_seven_liter = objectList?.reduce((accumulator ,item) => {
        return accumulator += item.seven_liter;
        }, 0)
    const getUniqueNames = () => {
        return objectList?.map(item => item.name)
            .filter((value, index, self) => value && self.indexOf(value) === index) || [];
    };

    // Add sorting state
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const selectedShopId = useSelector((state: any) => state.user.selectedShop);
    const shops = useSelector((state: any) => state.shop.shops);
    const selectedShop = shops.find((shop: any) => shop.id === selectedShopId);
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterContainerRef.current && !filterContainerRef.current.contains(event.target as Node)) {
                setShowFilters(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        dispatch(getShopsByUser(user.sub))
    }, []);
    const handleFilterToggle = (productName: string) => {
        const newFilters = selectedFilters.includes(productName)
            ? selectedFilters.filter(name => name !== productName)
            : [...selectedFilters, productName];
        
        setSelectedFilters(newFilters);
        localStorage.setItem(`preview_filters_${window.location.pathname}`, JSON.stringify(newFilters));
    };

    const handleSelectAllFilters = (checked: boolean) => {
        const newFilters = checked ? getUniqueNames() : [];
        setSelectedFilters(newFilters);
        localStorage.setItem(`preview_filters_${window.location.pathname}`, JSON.stringify(newFilters));
    };

    const uniqueProductNames = useMemo(() => {
        return Array.from(new Set(objectList?.map(item => item.name))) || [];
    }, [objectList]);

    // Update filteredStock to include sorting
    const filteredStock = useMemo(() => {
        const filtered = objectList?.filter(stock => {
            const matchesSearch = stock.name.toLowerCase().includes(search.toLowerCase());
            const matchesFilters = selectedFilters.length === 0 || selectedFilters.includes(stock.name);
            return matchesSearch && matchesFilters;
        }) || [];

        return [...filtered].sort((a, b) => {
            let comparison = 0;
            const value1 = a[sortField];
            const value2 = b[sortField];

            if (typeof value1 === 'number' && typeof value2 === 'number') {
                comparison = value1 - value2;
            } else {
                comparison = String(value1).localeCompare(String(value2));
            }
            
            return sortOrder === 'asc' ? comparison : -comparison;
        });
    }, [objectList, search, selectedFilters, sortField, sortOrder]);

    const allChecked = checkedStocks.length === filteredStock.length && filteredStock.length > 0;
    
    const setAllChecked = (checked: boolean) => {
        if(checked)
            setCheckedStocks(filteredStock.map(stock => stock.id));
        else
            setCheckedStocks([]);
    }
    const setChecked = (checked: boolean, id: string) => {
        if(checked)
            setCheckedStocks([...checkedStocks, id]);
        else
            setCheckedStocks(checkedStocks.filter(stockId => stockId !== id));
    }

    // Add useEffect to handle header changes
    useEffect(() => {
        // This will force a re-render when headers change
        setCheckedStocks([]);
    }, [headers]);

    // Add sort handler
    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    const handleExportToCSV = () => {
        // Check if we're on the consumption page
        const isConsumptionPage = window.location.pathname.includes('consumption');
        console.log(window.location.pathname)
        console.log(isConsumptionPage)
        const isICProducts = hasICProducts || headers[5]?.field === 'percentage'
        console.log(isICProducts)
        // Define headers based on page type and product type
        let exportHeaders;
        if (isConsumptionPage) {
            exportHeaders = headers.map(header => header.header) 
        } else {
            exportHeaders = isICProducts 
                ? ['Name', '3L', '5L', '7L', 'Total Quantity', 'Total Liters']
                : ['Name', 'Type', 'Pack Count', 'Pack Size'];
        }
        
        // Convert data to CSV rows based on page type and product type
        const dataToProcess = isConsumptionPage? objectList : filteredStock;
        const dataRows = dataToProcess.map(stock => {
            if (isConsumptionPage) {
                if(isICProducts){
                    console.log(stock)
                return [
                    stock.name,
                    stock.three_liter,
                    stock.five_liter,
                    stock.seven_liter,
                    stock.total_liters,
                    stock.percentage
                ]}
                else{
                    return [stock.name, stock.total_quantity]
                }
            } else if (isICProducts) {
                return [
                    stock.name,
                    stock.three_liter,
                    stock.five_liter,
                    stock.seven_liter,
                    stock.total_quantity,
                    stock.total_liters
                ];
            } else {
                return [
                    stock.name,
                    stock.type == 1 ? 'Food' : 'Other',
                    stock.pack_count,
                    stock.pack_size
                ];
            }
        });
        
        // Combine headers and data
        const csvContent = [
            exportHeaders.join(','),
            ...dataRows.map(row => row.join(','))
        ].join('\n');
        
        // Create a Blob with the CSV data
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        
        // Create a download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Set filename based on page type
        const pageType = isConsumptionPage ? 'consumption' : 'stock'
        const productType = isICProducts ? 'ic' : 'op'
        const date = new Date().toISOString().split('T')[0];
        const hours = new Date().getHours() < 10 ? `0${new Date().getHours()}` : new Date().getHours()
        const minutes = new Date().getMinutes() < 10 ? `0${new Date().getMinutes()}` : new Date().getMinutes()
        const filename = `${pageType}_${selectedShop?.name}_${productType}_${date}_${hours}_${minutes}.csv`
        link.download = filename;
        
        // Trigger the download
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    return (
        <div className="preview-table">
            <div className="preview-table__upper">
                <span>Edit columns</span>
                <div className="preview-table__upper__controls">
                    <IconField iconPosition="left">
                        <InputIcon className="pi pi-search"> </InputIcon>
                        <InputText placeholder="Search..." className="preview-table__upper__controls__filter-button" onChange={(e)=>setSearch(e.target.value)} />
                    </IconField>
                    <div className="preview-table__filter-container" ref={filterContainerRef}>
                        <Button 
                            onClick={() => setShowFilters(!showFilters)} 
                            icon="pi pi-filter" 
                            label={'Filters'} 
                            className={`preview-table__upper__controls__filter-button ${showFilters ? 'p-button-highlighted' : ''} ${selectedFilters.length > 0 ? 'filters-active' : ''}`} 
                        />
                        {showFilters && (
                            <div className="preview-table__filters-popup">
                                <div className="preview-table__filters-popup__item">
                                    <Checkbox 
                                        checked={selectedFilters.length === uniqueProductNames.length}
                                        onChange={(e) => handleSelectAllFilters(e.checked)}
                                        id="preview-filter-select-all"
                                    />
                                    <label htmlFor="preview-filter-select-all">Select All</label>
                                </div>
                                {uniqueProductNames.map(name => (
                                    <div key={name} className="preview-table__filters-popup__item">
                                        <Checkbox 
                                            checked={selectedFilters.includes(name)}
                                            onChange={() => handleFilterToggle(name)}
                                            id={`preview-filter-${name}`}
                                        />
                                        <label htmlFor={`preview-filter-${name}`}>{name}</label>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <Button onClick={() => handleExportToCSV()} icon="pi pi-download" iconPos='left' label={'Export'} className="preview-table__upper__controls__filter-button" />
                </div>
           </div>
            <div className="preview-table__table">
                <div className="preview-table__table__header">
                    <Checkbox onChange={e => setAllChecked(e.checked)} checked={allChecked}></Checkbox>
                    {headers.map(header => (
                        <div 
                            key={uuidv4()} 
                            className={`preview-table__table__header__cell ${header.style} sortable-header`}
                            onClick={() => handleSort(header.field)}
                        >
                            {header.header}
                            {sortField === header.field && 
                                <i className={`pi pi-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                            }
                        </div>
                    ))}
                </div>
                <div className="preview-table__table__body">
                {filteredStock.map((stock) => (
                    <PreviewTableRow key={uuidv4()} object={stock} headers={headers} checked={checkedStocks.includes(stock.id)} setChecked={(checked, id)=>setChecked(checked, id)}/>
                ))}
                </div>
                {objectList?.length > 0 && hasICProducts && (
                    <div className="preview-table__table__footer">
                        <div className="preview-table__table__footer__summary-row">
                            {[
                                { label: '7L', value: global_seven_liter },
                                { label: '5L', value: global_five_liter },
                                { label: '3L', value: global_three_liter },
                                { label: 'Total Quantity', value: global_total_quantity },
                                { label: 'Total Liters', value: `${global_total_liters}L` }
                            ].map((item) => (
                                <div key={`footer-${item.label}`} className="bold">
                                    {item.value}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
}
         
export default PreviewTable;